import type { ChronologyEvent } from "~/api/model";
import { formatChronologyEventDate } from "./chronologyRequest";

/**
 * Formats a list of chronology events into an itemised Markdown list suitable
 * for copy/export. Each event becomes a block with a bold date+detail heading
 * followed by metadata lines for event type, provider/facility, category and
 * source reference. Empty fields are omitted to keep the output compact.
 */
export function formatChronologyAsMarkdown(events: ChronologyEvent[]): string {
  if (events.length === 0) return "";

  const blocks = events.map((event) => {
    const date = formatChronologyEventDate(event) ?? "Undated";
    const detail = event.detail.trim();

    const heading = detail ? `**${date}** — ${detail}` : `**${date}**`;

    const meta: string[] = [];

    if (event.eventType.trim()) {
      meta.push(`Event Type: ${event.eventType.trim()}`);
    }

    const providerParts: string[] = [];
    if (event.provider.trim()) providerParts.push(event.provider.trim());
    if (event.facility.trim()) providerParts.push(event.facility.trim());
    if (providerParts.length > 0) {
      meta.push(`Provider: ${providerParts.join(" · ")}`);
    }

    if (event.documentName.trim()) {
      meta.push(`Document: ${event.documentName.trim()}`);
    }

    if (event.documentCategory.trim()) {
      meta.push(`Category: ${event.documentCategory.trim()}`);
    }

    const source = formatSource(event);
    if (source) {
      meta.push(`Source: ${source}`);
    }

    const metaBlock =
      meta.length > 0 ? meta.map((line) => `  ${line}`).join("  \n") : "";

    return metaBlock ? `${heading}  \n${metaBlock}` : heading;
  });

  return blocks.join("\n\n") + "\n";
}

function formatSource(event: ChronologyEvent): string | null {
  const { pageNumber, batesNumber } = event.reference;
  const displayPage = pageNumber != null ? pageNumber + 1 : null;
  if (displayPage != null && batesNumber) {
    return `Page ${displayPage} · ${batesNumber}`;
  }
  if (displayPage != null) {
    return `Page ${displayPage}`;
  }
  if (batesNumber) {
    return batesNumber;
  }
  return null;
}
